import type { PluginOption } from 'vite';
import { readFile } from '@wix/cli-fs';
import { join, basename } from 'node:path';
import type { SiteComponentElementItem } from '@wix/cli-app-definitions';
import {
  RELATIVE_ASSET_PATH_REGEX,
  ELEMENTS_DIR,
} from '@wix/cli-app-definitions';
import { generateUrl } from '@wix/cli-app-manifest';
import { ElementType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import type { ProjectModel } from '../../model.js';

async function processPresetsThumbnailUrls(
  presets: Record<string, { thumbnailUrl?: string }>,
  basePath: string,
  emitAssetFile: (assetUrl: string, relativePath: string) => Promise<string>
): Promise<void> {
  for (const preset of Object.values(presets)) {
    if (
      preset.thumbnailUrl &&
      RELATIVE_ASSET_PATH_REGEX.test(preset.thumbnailUrl)
    ) {
      preset.thumbnailUrl = await emitAssetFile(preset.thumbnailUrl, basePath);
    }
  }
}

async function processElementsThumbnailUrls(
  elements: Record<string, SiteComponentElementItem>,
  basePath: string,
  emitAssetFile: (assetUrl: string, relativePath: string) => Promise<string>
): Promise<void> {
  for (const [elementKey, elementItem] of Object.entries(elements)) {
    const elementPath = join(basePath, ELEMENTS_DIR, elementKey);
    if (elementItem.elementType === ElementType.inlineElement) {
      if (elementItem.inlineElement.presets) {
        await processPresetsThumbnailUrls(
          elementItem.inlineElement.presets,
          elementPath,
          emitAssetFile
        );
      }

      if (elementItem.inlineElement.elements) {
        await processElementsThumbnailUrls(
          elementItem.inlineElement.elements,
          elementPath,
          emitAssetFile
        );
      }
    }

    if (elementItem.elementType === ElementType.refElement) {
      if (elementItem.refElement.resolvedElement?.presets) {
        await processPresetsThumbnailUrls(
          elementItem.refElement.resolvedElement.presets,
          elementPath,
          emitAssetFile
        );
      }

      if (elementItem.refElement.resolvedElement?.elements) {
        await processElementsThumbnailUrls(
          elementItem.refElement.resolvedElement.elements,
          elementPath,
          emitAssetFile
        );
      }
    }
  }
}

export function configExtensionsFileEmitter(model: ProjectModel): PluginOption {
  return {
    name: 'config-extensions-file-emitter',
    async generateBundle() {
      const emitAssetFile = async (assetUrl: string, relativePath: string) => {
        const resolved = await this.resolve(assetUrl, relativePath);

        if (resolved == null) {
          return assetUrl;
        }

        const content = new Uint8Array(await readFile(resolved.id));

        const fileName = this.emitFile({
          type: 'asset',
          name: basename(resolved.id),
          originalFileName: assetUrl,
          source: content,
        });

        return generateUrl(this.getFileName(fileName));
      };

      for (const component of model.components) {
        if (
          component.type === 'SitePlugin' &&
          component.marketData.logoUrl?.match(RELATIVE_ASSET_PATH_REGEX)
        ) {
          component.marketData.logoUrl = await emitAssetFile(
            component.marketData.logoUrl,
            component.relativePath
          );
        }

        if (
          component.type === 'CustomElement' &&
          component.presets?.some((preset) =>
            preset.thumbnailUrl.match(RELATIVE_ASSET_PATH_REGEX)
          )
        ) {
          for (const preset of component.presets) {
            if (RELATIVE_ASSET_PATH_REGEX.test(preset.thumbnailUrl)) {
              preset.thumbnailUrl = await emitAssetFile(
                preset.thumbnailUrl,
                component.relativePath
              );
            }
          }
        }

        if (component.type === 'SiteComponent' && component.presets) {
          await processPresetsThumbnailUrls(
            component.presets,
            component.relativePath,
            emitAssetFile
          );
        }

        if (
          component.type === 'SiteComponent' &&
          Object.keys(component.elements).length
        ) {
          await processElementsThumbnailUrls(
            component.elements,
            component.relativePath,
            emitAssetFile
          );
        }
      }
    },
  };
}
